import React from 'react';
import { useTranslation } from 'react-i18next';
import { SocialIcon, normalizePlatformName, type SocialPlatform } from '../icons/SocialIcon';

interface GoToMarketStrategy {
  marketScope?: string;
  businessModel?: string;
  distributionType?: string;
}

interface RecommendedNetwork {
  platform: string;
  frequency?: string;
}

interface BrandBlock {
  goToMarketStrategy?: GoToMarketStrategy;
  targetSegments?: string[];
  personaTypes?: string[];
  priorityMarkets?: string[];
  contentTone?: string;
  imageStyle?: string;
  usp?: string[];
  recommendedSocialNetworks?: RecommendedNetwork[];
  contentThemes?: string[];
  keyInsights?: string[];
}

export interface BrandSummaryPayload {
  id?: string;
  name?: string;
  logo?: string;
  brand?: BrandBlock;
}

interface BrandSummaryCardProps {
  data: BrandSummaryPayload;
}

/**
 * BrandSummaryCard
 *
 * Compact overview of a brand strategy payload returned by the backend.
 * Designed to be robust to partially filled data.
 */
export const BrandSummaryCard: React.FC<BrandSummaryCardProps> = ({ data }) => {
  const { t } = useTranslation();

  const brand = data?.brand || {};
  const gtm = brand.goToMarketStrategy || {};
  const capitalizeFirst = (value?: string): string => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };
  const tagPill = (label: string, key?: string | null) => (
    key && key.trim() ? (
      <span key={`${label}-${key}`} className="text-[11px] px-2 py-0.5 rounded-full border border-black bg-white">
        <strong className="mr-1">{label}:</strong>
        <span>{key}</span>
      </span>
    ) : null
  );

  const renderList = (items?: string[], emptyLabel?: string) => {
    const arr = Array.isArray(items) ? items.filter(Boolean) : [];
    if (arr.length === 0) return emptyLabel ? (<span className="text-sm text-gray-500">{emptyLabel}</span>) : null;
    return (
      <ul className="list-disc list-inside space-y-1 text-sm text-gray-800">
        {arr.map((it, idx) => (
          <li key={`${it}-${idx}`}>{it}</li>
        ))}
      </ul>
    );
  };

  const renderTagList = (items?: string[], emptyLabel?: string) => {
    const arr = Array.isArray(items) ? items.filter(Boolean) : [];
    if (arr.length === 0) return emptyLabel ? (<span className="text-sm text-gray-500">{emptyLabel}</span>) : null;
    return (
      <div className="flex flex-wrap gap-2">
        {arr.map((it, idx) => (
          <span key={`${it}-${idx}`} className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border border-black bg-white text-gray-800">
            {it}
          </span>
        ))}
      </div>
    );
  };

  const recommended = Array.isArray(brand.recommendedSocialNetworks) ? brand.recommendedSocialNetworks : [];

  // Build Marketing Strategy tags similar to Brands page
  const marketingTags: Array<{ emoji: string; text: string }> = [];
  if (gtm.marketScope) {
    const scope = (gtm.marketScope || '').toLowerCase();
    marketingTags.push({
      emoji: scope === 'local' ? '🏠' : scope === 'national' ? '🇱🇺' : '🌍',
      text: gtm.marketScope
    });
  }
  if (gtm.businessModel) {
    const bm = (gtm.businessModel || '').toUpperCase();
    marketingTags.push({ emoji: bm === 'B2B' ? '🏢' : bm === 'B2C' ? '👥' : '💼', text: gtm.businessModel! });
  }
  if (gtm.distributionType) {
    const dist = (gtm.distributionType || '').toLowerCase();
    marketingTags.push({ emoji: dist === 'physical' ? '🏪' : dist === 'digital' ? '💻' : '🔄', text: gtm.distributionType! });
  }
  const priorityMarkets = Array.isArray(brand.priorityMarkets) ? brand.priorityMarkets.filter(Boolean) : [];
  priorityMarkets.forEach((m) => marketingTags.push({ emoji: '🎯', text: m }));

  return (
    <section className="w-full bg-white rounded-xl border border-black shadow-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900">{t('brands.marketingStrategy', 'Marketing Strategy')}</h3>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {data?.logo && (
            <img src={data.logo} alt={data?.name || 'Brand'} className="w-6 h-6 rounded object-cover" />
          )}
          <span className="truncate">{data?.name}</span>
        </div>
      </div>

      {/* Marketing strategy tags */}
      {marketingTags.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-1">{t('brand.marketScope', 'Market Scope')}</h4>
          <div className="flex flex-wrap gap-2">
            {marketingTags.map((tag, index) => (
              <span key={`${tag.text}-${index}`} className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 rounded-lg text-sm">
                <span className="text-sm">{tag.emoji}</span>
                <span className="capitalize">{tag.text}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Persona Types (blue tags) */}
      {Array.isArray(brand.personaTypes) && brand.personaTypes.filter(Boolean).length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-1">{t('brand.personaTypes', 'Persona Types')}</h4>
          <div className="flex flex-wrap gap-2">
            {brand.personaTypes!.filter(Boolean).map((p, idx) => (
              <span key={`${p}-${idx}`} className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-lg text-sm">
                <span>{p}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Segments / Personas / Markets */}
      {/* Removed extra Target Segments / Priority Markets section per request */}

      {/* Tone and Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">{t('brand.contentTone', 'Content Tone')}</h4>
          <p className="text-sm text-gray-800">{capitalizeFirst(brand.contentTone) || t('common.notAvailable', 'N/A')}</p>
        </div>
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">{t('brand.imageStyle', 'Image Style')}</h4>
          <p className="text-sm text-gray-800">{capitalizeFirst(brand.imageStyle) || t('common.notAvailable', 'N/A')}</p>
        </div>
      </div>

      {/* Unique selling propositions */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-1">{t('brand.usp', 'Unique Selling Proposition')}</h4>
        <div className="flex flex-wrap gap-2">
          {(Array.isArray(brand.usp) ? brand.usp.filter(Boolean) : []).map((it, idx) => (
            <span key={`${it}-${idx}`} className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-50 to-indigo-50 text-indigo-700 rounded-lg text-sm">
              {it}
            </span>
          ))}
        </div>
      </div>

      {/* Key Insights removed as requested */}

      {/* Content Themes */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-1">{t('brand.contentThemes', 'Content Themes')}</h4>
        <div className="flex flex-wrap gap-2">
          {(Array.isArray(brand.contentThemes) ? brand.contentThemes.filter(Boolean) : []).map((it, idx) => (
            <span key={`${it}-${idx}`} className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-50 to-indigo-50 text-indigo-700 rounded-lg text-sm">
              {it}
            </span>
          ))}
        </div>
      </div>

      {/* Recommended networks */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('brand.recommendedNetworks', 'Recommended Social Networks')}</h4>
        {recommended.length === 0 ? (
          <p className="text-sm text-gray-500">{t('common.notAvailable', 'N/A')}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {recommended.map((r, idx) => {
              const platform = normalizePlatformName(r.platform) as SocialPlatform;
              return (
                <span key={`${r.platform}-${idx}`} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full bg-white border border-gray-300 text-gray-700">
                  <SocialIcon platform={platform} size={12} />
                  <span className="hidden sm:inline">{r.frequency || ''}</span>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default BrandSummaryCard;


